import { useLocation } from "react-router";
import { LogOut, PanelLeft, Sun, Moon, Monitor } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAdminPageMeta } from "@/lib/routes/admin-route-catalog";
import { HeaderActionsSlot } from "./HeaderActions";

interface AdminHeaderProps {
  onOpenNavigation?: () => void;
  openerRef?: React.RefObject<HTMLButtonElement | null>;
}

export default function AdminHeader({ onOpenNavigation, openerRef }: AdminHeaderProps) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const meta = getAdminPageMeta(location.pathname);

  const nextTheme = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
  const ThemeIcon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;

  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "AD";

  return (
    <header
      className="header header-wrapper border-b border-border/60 bg-card/95 px-6 backdrop-blur"
      style={{ minHeight: "var(--admin-header-height)" }}
    >
      <div className="flex items-center gap-3">
        <Button
          ref={openerRef}
          type="button"
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label="Open navigation"
          onClick={onOpenNavigation}
        >
          <PanelLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {meta.section}
          </p>
          <p className="mt-0.5 text-sm font-medium text-foreground">{meta.title}</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <HeaderActionsSlot />
        {/* `xl`, not `lg`: at 1024 px the placeholder plus a page's actions overflow (T14). */}
        <div className="hidden xl:block">
          <Input
            disabled
            aria-label="Search coming soon"
            placeholder="Search users, subscriptions, prompts..."
            className="w-80 opacity-70"
          />
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Toggle theme (current: ${theme})`}
          onClick={() => setTheme(nextTheme)}
        >
          <ThemeIcon className="h-5 w-5" />
        </Button>
        <span className="hidden min-[480px]:inline-block rounded-full border border-border/60 bg-background px-3 py-1 text-xs font-semibold tracking-[0.16em] text-muted-foreground">
          {user?.role}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-10 w-10 rounded-full">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.picture || undefined} alt={user?.name || "Admin"} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.name || "Admin"}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
